import React from 'react';
import { getTpl, getTheme } from './data';
import AbstractScene from './AbstractScene';
import EditorialScene from './EditorialScene';
import CustomCardScene from './CustomCardScene';

// VoucherCard — driven by three independent axes:
//   - theme (palette)        → CSS variables --v-bg, --v-ink, --v-accent
//   - aesthetic (composition) → wraps everything in `.v2-card.aes-{id}`
//   - template (occasion)    → glyph + tagline + abstract preset key
//
// Three aesthetics + Custom: editorial (illustrated scene per occasion),
// abstract (pattern per occasion), custom (salon-built free-form). Minimal
// was dropped — it was functionally one composition × 12 colours and was
// redundant with Custom's solid-colour path.

const VoucherCard = ({
  voucher,
  aesthetic = 'editorial',
  salonName = 'Rouge Parlour',
  currency = '£',
  showPromo = true,
  themeOverride,
  // Canvas-editor pass-through props. The Custom Designer's preview
  // passes interactive=true plus per-element handlers; non-designer
  // uses ignore them entirely.
  interactive = false,
  selectedId,
  selectedIds,
  editingId,
  onElementSelect,
  onElementChange,
  onElementsChange,
  onElementDuplicate,
  onElementRemove,
  onElementDoubleClick,
  onElementContextMenu,
  onElementCommitEdit,
  onBgPhotoChange,
}) => {
  const tpl = getTpl(voucher.template);
  const theme = getTheme(themeOverride || voucher.theme || tpl.suggestedTheme);
  const inkDark = theme.ink === 'dark';
  const face = voucher.value;
  const pay = voucher.promo;
  const hasPromo = showPromo && pay != null && pay < face;

  const cssVars = {
    '--v-bg':     theme.bg,
    '--v-ink':    inkDark ? '#1A1A1E' : '#FFFFFF',
    '--v-accent': theme.accent,
    '--v-sub':    theme.sub,
  };

  const cls = [
    'v2-card',
    `aes-${aesthetic}`,
    `theme-${theme.id}`,
    `hero-${theme.hero}`,
    inkDark ? 'ink-dark' : 'ink-light',
  ].join(' ');

  return (
    <div className={cls} style={cssVars}>
      {/* Editorial — premium illustrated SVG scene per occasion (cake /
          snowman / hearts / rings / etc.). The scene paints the full card
          background; only the amount + optional promo float on top. The
          occasion LABEL (BIRTHDAY / MOTHER'S DAY / ...) is intentionally
          NOT rendered — the illustration carries the occasion cue, and
          dropping the text keeps each card versatile (a salon can sell
          a "birthday" design to a customer who'd use it for anything). */}
      {aesthetic === 'editorial' && (
        <>
          <div className="v2-abs-scene">
            <EditorialScene occasion={voucher.template} />
          </div>
          {hasPromo && <div className="v2-eill-promo">Pay {currency}{pay}</div>}
          <div className="v2-eill-amount">
            <span className="v2-eill-currency">{currency}</span>
            <span className="v2-eill-number">{face}</span>
          </div>
        </>
      )}

      {/* Abstract — vibrant pattern per occasion. Only the amount (and
          optional promo pill) float on top. Like Editorial, the
          occasion LABEL is intentionally NOT rendered — the pattern
          carries the mood, and dropping the text keeps each card
          versatile (no implied "this is only for Valentine's"). */}
      {aesthetic === 'abstract' && (
        <>
          <div className="v2-abs-scene">
            <AbstractScene occasion={voucher.template} />
          </div>
          {hasPromo && <div className="v2-eill-promo">Pay {currency}{pay}</div>}
          <div className="v2-eill-amount">
            <span className="v2-eill-currency">{currency}</span>
            <span className="v2-eill-number">{face}</span>
          </div>
        </>
      )}

      {/* Custom — fully delegated to CustomCardScene. The scene owns
          layout / background / typography for the salon-built design,
          and renders the amount itself so each layout can place it
          where the composition demands. */}
      {aesthetic === 'custom' && voucher.customDesign && (
        <CustomCardScene
          design={voucher.customDesign}
          value={face}
          currency={currency}
          promo={hasPromo ? pay : null}
          editable={interactive}
          selectedId={selectedId}
          selectedIds={selectedIds}
          editingId={editingId}
          onSelect={onElementSelect}
          onElementChange={onElementChange}
          onElementsChange={onElementsChange}
          onElementDuplicate={onElementDuplicate}
          onElementRemove={onElementRemove}
          onElementDoubleClick={onElementDoubleClick}
          onElementContextMenu={onElementContextMenu}
          onCommitEdit={onElementCommitEdit}
          onBgPhotoChange={onBgPhotoChange}
        />
      )}

      {/* Custom but no design saved yet — placeholder so layout doesn't break */}
      {aesthetic === 'custom' && !voucher.customDesign && (
        <div className="v2-custom-empty">
          <span>Custom design</span>
          <small>Build one in the Designs tab</small>
        </div>
      )}
    </div>
  );
};

export default VoucherCard;
